import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

interface BetaDisclaimerModalProps {
  open: boolean;
  onAccepted: () => void;
}

export default function BetaDisclaimerModal({ open, onAccepted }: BetaDisclaimerModalProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const acceptMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/user/accept-disclaimer");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      onAccepted();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to accept disclaimer. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleDisagree = () => {
    window.location.href = "https://www.google.com";
  };

  const handleAgree = () => {
    acceptMutation.mutate();
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl">Welcome — Terms &amp; Disclaimer</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4 text-sm text-muted-foreground">
                <p>
                  This is a beta-deployment of this app. The app is still in its early stages of
                  testing and development. The number of beta-testers is limited. A paywall will
                  be applied once the app has been considered to be fully deployable.
                </p>

                <p>
                  No part of this app should be considered an endorsement by any third party.
                  ForgeRestore is not affiliated in any way with AA, NA, or CMA nor have these
                  groups taken any part in its development.
                </p>

                <p className="font-medium text-foreground">
                  ForgeRestore is a peer-recovery organizing tool for general wellness use. It
                  is not a medical device, not a software-as-a-medical-device, not a clinical
                  decision-support system, not a therapeutic service, and not a diagnostic
                  service. Nothing in the app — including the Burning Desire button, the
                  Recovery Playbook, trigger and craving trackers, sponsor communication, the
                  deterministic pattern reflections and weekly/monthly digests computed from
                  your own data, or the check-in indicator tiles you build from your own data —
                  is medical, psychiatric, or clinical advice, and none
                  of it is a substitute for a sponsor, a meeting, a therapist, a physician, or
                  emergency services.
                </p>

                <p>
                  This app is meant to support you in organizing your own recovery activity.
                  It makes no claim or warranty, inherent or implied, that it will prevent
                  relapse or control or maintain your sobriety.
                </p>

                <p>
                  If you or someone else is in immediate physical danger, stop using the app and
                  call your local emergency number (911 in the U.S.) or the 988 Suicide &amp;
                  Crisis Lifeline.
                </p>

                <p className="font-medium text-foreground">
                  Your use of this app constitutes your understanding of this disclaimer and your
                  agreement that your use of the app is in acceptance and compliance within the
                  bounds of this disclaimer.
                </p>
                <p>
                  By agreeing, you also accept our{" "}
                  <button
                    type="button"
                    onClick={() => setLocation("/terms")}
                    className="underline text-foreground hover:text-primary"
                    data-testid="link-terms-disclaimer"
                  >
                    Terms &amp; Conditions
                  </button>{" "}
                  (which include eligibility, acceptable use, and the limitation of
                  liability) and our{" "}
                  <button
                    type="button"
                    onClick={() => setLocation("/privacy-policy")}
                    className="underline text-foreground hover:text-primary"
                    data-testid="link-privacy-policy-disclaimer"
                  >
                    Privacy Policy
                  </button>
                  , which explains what data we collect and your rights over it. You
                  confirm that you are at least 18 years old.
                </p>
              </div>
            </ScrollArea>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel 
            onClick={handleDisagree}
            data-testid="button-disclaimer-disagree"
          >
            Disagree
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleAgree}
            disabled={acceptMutation.isPending}
            data-testid="button-disclaimer-agree"
          >
            {acceptMutation.isPending ? "Processing..." : "Agree"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
